import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { fadeInOut, bounceInOut } from '../shared/animations';
import { trigger, transition, style, animate } from '@angular/animations';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from '../services/firestore-context.service';
import { AnalyticsService } from '../services/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { ApiService } from '../services/api.service';
import { ThemeService } from '../services/theme.service';
import { CurrencyService } from '../services/currency.service';
import { UxCamService } from '../services/uxcam.service';
import { environment } from '../../environments/environment';
import { carriedPaymentParams } from '../shared/payment-outcome';


@Component({
    selector: 'app-splash',
    templateUrl: './splash.component.html',
    styleUrls: ['./splash.component.scss'],
    animations: [
        fadeInOut,
        bounceInOut,
        trigger('pageTransition', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})

export class SplashComponent implements OnInit {
  clientId: string | null = null;
  tableId: string | null = null;
  tableIdentifier: string | null = null;
  locationId: string | null = null;
  orderingType: 'dine-in' | 'off-site' | 'delivery' = 'dine-in';
  clientData: any = null;
  loading: boolean = true;
  error: string | null = null;
  baseUrl: string = environment.baseUrl;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private analytics: AnalyticsService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private apiService: ApiService,
    private themeService: ThemeService,
    private currencyService: CurrencyService,
    private uxcam: UxCamService,
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.clientId = params['id'];
      this.tableId = params['tableId'];
      this.tableIdentifier = params['tableIdentifier'];
      this.locationId = params['locationId'];
      
      // Determine ordering type based on URL parameters
      this.determineOrderingType();
      
      this.authenticateUser();
    });
  }

  determineOrderingType(): void {
    // 1. Dine-In: has tableIdentifier
    if (this.tableIdentifier || this.tableId) {
      this.orderingType = 'dine-in';
    }
    // 2. Delivery: has locationId
    else if (this.locationId) {
      this.orderingType = 'delivery';
    }
    // 3. Off-Site: only has id (Marketing/Delivery/Pickup)
    else {
      this.orderingType = 'off-site';
    }
  }

  generateDeviceId(length: number = 24): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    return result;
  }

  authenticateUser(): void {

    let lastClientId = localStorage.getItem('lastClientId');
    // Keys that must survive a venue switch (per-device, not per-shop): the device id and the
    // diner's explicit language choice (devicePreferredLanguage). Everything else is per-shop
    // and gets wiped so a stale menu/currency can't leak into the new venue.
    const keepKeys = ['deviceGuid', 'devicePreferredLanguage'];

    /*
     * Keys that must survive a venue switch because they belong to an ORDER, not a shop.
     *
     * A guest can hold an unpaid card order at one venue and scan another venue's QR —
     * to read a menu, or because they walked next door — and the first order stays held
     * and payable server-side either way. Wiping these would delete the only local
     * record of it: the items, the order number, and the "pay again" route. Both key
     * families are already shop-scoped in their own names, so nothing can render against
     * the wrong venue.
     */
    const keepPrefixes = ['awaitingPayment_', 'pendingOrderNotification_'];
    const prefixedKeys: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && keepPrefixes.some(prefix => key.startsWith(prefix))) {
          prefixedKeys.push(key);
        }
      }
    } catch {
      /* storage denied — nothing to preserve, and nothing to fail over */
    }

    /*
     * A different backend is a different database, so it is a venue switch too — even when
     * the venue id is the same. Two backends can both have a venue 84, and the saved order id
     * from one is some other guest's old order on the other: a stranger's lines and total, and
     * an order refused as long placed. Only reachable by pointing a dev build at another
     * backend; `baseUrl` never changes in production.
     *
     * The order-scoped keys go too: their order ids belong to the old database.
     */
    const lastBaseUrl = localStorage.getItem('lastBaseUrl');
    const backendChanged = lastBaseUrl !== null && lastBaseUrl !== this.baseUrl;

    const keptEntries = [...keepKeys, ...(backendChanged ? [] : prefixedKeys)]
      .map(k => [k, localStorage.getItem(k)] as [string, string | null]);

    if (lastClientId !== this.clientId || backendChanged) {
      localStorage.clear();
      for (const [k, v] of keptEntries) {
        if (v !== null) {
          localStorage.setItem(k, v);
        }
      }
      // Switching to a different shop: wipe the previous shop's cached, per-shop
      // sessionStorage (currencySymbol, clientData, menu caches, table/location).
      // Otherwise a stale currency symbol / menu leaks into the new shop.
      // A fresh authToken is fetched and re-set right below, so a full clear is safe.
      sessionStorage.clear();
    }
    localStorage.setItem('lastBaseUrl', this.baseUrl);
    

    let guid = localStorage.getItem('deviceGuid');
    if (!guid) {
      guid = this.generateDeviceId(); // Generate a random ID
      localStorage.setItem('deviceGuid', guid);
    }

    // First visit: the recorder booted before this guid existed, so name the session now.
    this.uxcam.setUserIdentity(guid);

    // `clientId` is passed explicitly here: this fires before the shop id is written to
    // sessionStorage, so the service's base bundle cannot pick it up yet.
    this.analytics.log('client_open', { venue_id: this.clientId });

    const authUrl = `${this.baseUrl}api/v2/device/login`;
    const authData = {
      guid: guid,
      os_type: 3,
      is_extension: 0
    };

    this.http.post<any>(authUrl, authData).subscribe({
      next: (response) => {
        if (response && response.token) {

          sessionStorage.setItem('authToken', response.token);

          this.loadClientData();
        } else {
          this.error = "Authentication failed. Please try again.";
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Authentication error:', error);
        this.error = "Authentication failed. Please try again.";
        this.loading = false;
      }
    });
  }

  loadClientData(): void {
    if (!this.clientId) {
      this.error = "Missing client ID. Please check the URL.";
      this.loading = false;
      return;
    }

    this.apiService.getClientData(Number(this.clientId)).subscribe({
      next: async (response) => {
        sessionStorage.setItem('clientId', this.clientId!);
        localStorage.setItem('lastClientId', this.clientId!);
        this.analytics.setShop(this.clientId);
        this.uxcam.setShop(this.clientId);
        if (this.tableId) {
          sessionStorage.setItem('tableId', this.tableId);
        }
        if (this.tableIdentifier) {
          sessionStorage.setItem('tableIdentifier', this.tableIdentifier);
        }
        if (this.locationId) {
          sessionStorage.setItem('locationId', this.locationId);
        }
        // Store ordering type
        sessionStorage.setItem('orderingType', this.orderingType);
        // Store client data for later use
        this.clientData = response;
        sessionStorage.setItem('clientData', JSON.stringify(response));
        // Apply the venue's dynamic menu theme before navigating to the menu.
        this.themeService.applyTheme(response);
        // Derive the currency symbol from THIS shop's data so a previous shop's
        // symbol can never leak in (updates the observable + sessionStorage).
        this.currencyService.loadCurrency(response);
        this.analytics.log('loading_shown');
        this.checkAndStorePromotion();
        this.loading = false;
        // `menu_open` used to fire here. Dropped: no other client sends it, no dashboard query
        // reads it, and it duplicated `client_open` + `loading_shown`. `menu_presented` (from
        // MenuComponent) is the event that actually measures a menu being seen.
        const redirect = this.route.snapshot.queryParams['redirect'];
        if (redirect) {
          /*
           * ⚠️ The remaining query params have to travel with the redirect.
           *
           * BORICA returns the guest to the ROOT — /?id=<shop>&redirect=order-details
           * &payment=…&order=… — because the app boots at the root and nothing works
           * until this component has loaded the venue. A bare navigateByUrl('/' + redirect)
           * drops everything after `redirect`, so the order screen used to arrive with no
           * idea a payment had just happened and simply showed an empty cart.
           *
           * Built through a UrlTree rather than router.navigate([...]) so a redirect
           * carrying its own path segments or query string keeps them.
           */
          const tree = this.router.parseUrl('/' + redirect);
          tree.queryParams = {
            ...tree.queryParams,
            ...carriedPaymentParams(this.route.snapshot.queryParams),
          };
          this.router.navigateByUrl(tree);
          return;
        }

        /*
         * An outstanding card payment with no `redirect` param — the guest closed
         * BORICA's page and simply re-opened the app.
         *
         * Without this they land on /menu, OrderDetailsComponent never mounts, and the
         * whole recovery path never runs: empty cart, no explanation, and an order still
         * held server-side until the sweep cancels it. Abandonment is the commonest
         * failure there is, so it cannot depend on the guest returning tidily.
         *
         * ⚠️ Only for a record that is still ACTIONABLE. A settled one lives for a day,
         * and redirecting on those would hijack the venue's QR — a guest scanning
         * tomorrow to read the menu would be dumped on yesterday's dead payment banner.
         */
        if (this.hasActionablePayment()) {
          this.router.navigate(['/order-details']);
          return;
        }

        this.router.navigate(['/menu']);
      },
      error: (error) => {
        console.error('Error loading client data:', error);
        this.error = "Failed to load restaurant data. Please try again.";
        this.loading = false;
      }
    });
  }

  /**
   * Whether this venue has an outstanding payment worth interrupting the boot for.
   *
   * Read directly rather than through AwaitingPaymentStore: Splash runs before the
   * app has a venue context, and this needs to be a cheap, total-failure-tolerant
   * look at storage rather than a service call.
   */
  private hasActionablePayment(): boolean {
    if (!this.clientId) {
      return false;
    }

    try {
      const raw = localStorage.getItem(`awaitingPayment_${this.clientId}`);
      if (!raw) {
        return false;
      }

      const record = JSON.parse(raw);

      // Settled means the backend has already given its final answer. Show it if the
      // guest goes to the order screen, but never drag them there.
      return !!record && !!record.orderId && !record.settled;
    } catch {
      return false;
    }
  }

  checkAndStorePromotion(): void {
    if (!this.clientId) return;

    const businessId = parseInt(this.clientId, 10);
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    this.fsContext.run(() =>
      this.afs.collection('before-order-promotion', ref =>
        ref.where('businessId', '==', businessId).where('active', '==', true)
      ).get()
    ).subscribe(snapshot => {
      if (!snapshot.empty) {
        const promoData = snapshot.docs[0].data() as any;
        const promoId = promoData.id;
        const promoKey = `promoShown_${promoId}_${today}`;

        // Check if this specific promo was already shown today
        if (localStorage.getItem(promoKey)) {
          return;
        }

        localStorage.setItem('beforeOrderPromotion', JSON.stringify(promoData));
        localStorage.setItem(promoKey, 'true');
      }
    });
  }

} 